/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.sessions

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.data.repository.cauce.SessionRepository
import studio.oddpods.cauce.core.data.util.BusinessError
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.model.patient.Patient
import studio.oddpods.cauce.core.model.session.CreateSessionRequest
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.sessions.generated.resources.Res
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_error_fee_invalid
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_error_limit_reached
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_error_load_patients
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_error_patient_required
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_error_save_failed

data class LogSessionState(
    val patients: List<Patient> = emptyList(),
    val selectedPatientId: String? = null,
    val sessionDate: LocalDate = kotlin.time.Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date,
    val feeInput: String = "",
    val sessionType: String = "REGULAR",
    val isSaving: Boolean = false,
    val isLoadingPatients: Boolean = true,
    val error: UiText? = null,
)

sealed class LogSessionEvent {
    data object SaveSuccess : LogSessionEvent()
}

sealed class LogSessionAction {
    data class PatientSelected(val patientId: String) : LogSessionAction()
    data class DateChanged(val date: LocalDate) : LogSessionAction()
    data class FeeChanged(val fee: String) : LogSessionAction()
    data class TypeChanged(val type: String) : LogSessionAction()
    data object SaveClicked : LogSessionAction()
}

class LogSessionViewModel(
    private val sessionRepository: SessionRepository,
    private val patientRepository: PatientRepository,
    private val currencyFormatter: CurrencyFormatter,
    val dateFormatter: DateFormatter,
) : BaseViewModel<LogSessionState, LogSessionEvent, LogSessionAction>(LogSessionState()) {

    init {
        loadPatients()
    }

    override fun handleAction(action: LogSessionAction) {
        when (action) {
            is LogSessionAction.PatientSelected -> {
                val patient = state.patients.find { it.id == action.patientId }
                mutableStateFlow.value = state.copy(
                    selectedPatientId = action.patientId,
                    feeInput = if (patient != null) (patient.defaultFee / 100).toString() else state.feeInput,
                    error = null,
                )
            }
            is LogSessionAction.DateChanged ->
                mutableStateFlow.value = state.copy(sessionDate = action.date, error = null)
            is LogSessionAction.FeeChanged ->
                mutableStateFlow.value = state.copy(feeInput = action.fee, error = null)
            is LogSessionAction.TypeChanged ->
                mutableStateFlow.value = state.copy(sessionType = action.type, error = null)
            LogSessionAction.SaveClicked -> handleSave()
        }
    }

    private fun loadPatients() {
        viewModelScope.launch {
            patientRepository.getPatients()
                .catch { e ->
                    mutableStateFlow.value = state.copy(
                        isLoadingPatients = false,
                        error = e.toUiText(Res.string.sessions_error_load_patients),
                    )
                }
                .collect { patients ->
                    mutableStateFlow.value = state.copy(patients = patients, isLoadingPatients = false)
                }
        }
    }

    private fun handleSave() {
        val patientId = state.selectedPatientId
        val feeCentavos = currencyFormatter.parse(state.feeInput)

        if (patientId == null) {
            mutableStateFlow.value = state.copy(error = UiText.Resource(Res.string.sessions_error_patient_required))
            return
        }
        if (feeCentavos == null || feeCentavos <= 0) {
            mutableStateFlow.value = state.copy(error = UiText.Resource(Res.string.sessions_error_fee_invalid))
            return
        }

        mutableStateFlow.value = state.copy(isSaving = true, error = null)
        viewModelScope.launch {
            sessionRepository.createSession(
                CreateSessionRequest(
                    patientId = patientId,
                    sessionDate = state.sessionDate.toString(),
                    fee = feeCentavos,
                    sessionType = state.sessionType,
                ),
            )
                .onSuccess {
                    mutableStateFlow.value = state.copy(isSaving = false)
                    sendEvent(LogSessionEvent.SaveSuccess)
                }
                .onFailure { e ->
                    val error = when (e) {
                        is BusinessError.LimitReached -> UiText.Resource(Res.string.sessions_error_limit_reached)
                        else -> e.toUiText(Res.string.sessions_error_save_failed)
                    }
                    mutableStateFlow.value = state.copy(isSaving = false, error = error)
                }
        }
    }
}
